'use strict';

const { computeRiskScoreFreshness } = require('./riskScoreFreshness');

/**
 * Canonical employee risk state — single schema for all UI surfaces.
 * Built from persisted turnover payloads only (no scoring here).
 */

function trimDriver(driver) {
  if (!driver || typeof driver !== 'object') return null;
  const detail = driver.detail != null ? String(driver.detail).trim() : '';
  if (!detail) return null;
  return {
    factorKey: driver.factorKey || null,
    category: driver.category || null,
    label: driver.label ? String(driver.label).trim() : 'Retention signal',
    detail,
    contributionPct:
      driver.contributionPct != null && Number.isFinite(Number(driver.contributionPct))
        ? Number(driver.contributionPct)
        : null,
    confidence: driver.confidence === 'inferred' ? 'inferred' : 'high',
  };
}

function normalizeLevel(level) {
  const l = String(level || '').toLowerCase();
  if (l === 'critical' || l === 'high' || l === 'moderate' || l === 'low') return l;
  if (l === 'med' || l === 'medium') return 'moderate';
  return null;
}

/**
 * @param {object|null|undefined} employee
 * @param {object|null|undefined} turnoverPayload — persisted turnover / risk_score_payload
 * @returns {object}
 */
function buildEmployeeRiskState(employee, turnoverPayload) {
  const turnover =
    turnoverPayload && typeof turnoverPayload === 'object'
      ? turnoverPayload
      : employee?.risk_score_payload && typeof employee.risk_score_payload === 'object'
        ? employee.risk_score_payload
        : employee?.turnover && typeof employee.turnover === 'object'
          ? employee.turnover
          : null;

  const employeeId = employee?.id ?? turnover?.employeeId ?? null;
  const employeeName =
    employee?.name != null
      ? String(employee.name)
      : turnover?.employeeName != null
        ? String(turnover.employeeName)
        : null;

  const scoreRaw = turnover?.score ?? employee?.risk_score;
  const riskScore = Number.isFinite(Number(scoreRaw)) ? Math.round(Number(scoreRaw)) : null;
  const riskLevel = normalizeLevel(turnover?.level ?? employee?.risk_level);
  const scoreReady = turnover?.scoreReady !== false && riskScore != null;

  const eligibilityStatus =
    turnover?.eligibility?.status != null
      ? String(turnover.eligibility.status)
      : turnover?.metadata?.eligibilityStatus != null
        ? String(turnover.metadata.eligibilityStatus)
        : null;

  const errorCategory =
    turnover?.metadata?.errorCategory != null ? String(turnover.metadata.errorCategory) : null;

  const processingStateRaw = String(turnover?.metadata?.processingState || '').toLowerCase();
  let processingState = 'pending';
  if (scoreReady) {
    processingState = 'ready';
  } else if (
    processingStateRaw === 'processing' ||
    processingStateRaw === 'queued' ||
    processingStateRaw === 'retrying'
  ) {
    processingState = processingStateRaw;
  } else if (processingStateRaw === 'failed' || errorCategory === 'processing_failure') {
    processingState = 'failed';
  } else if (
    processingStateRaw === 'insufficient_data' ||
    errorCategory === 'insufficient_data' ||
    eligibilityStatus === 'not_eligible'
  ) {
    processingState = 'insufficient_data';
  } else if (processingStateRaw === 'calculated') {
    processingState = 'pending';
  }

  const confidenceLevel =
    turnover?.confidenceLevel != null
      ? String(turnover.confidenceLevel)
      : turnover?.metadata?.confidenceLevel != null
        ? String(turnover.metadata.confidenceLevel)
        : null;

  const confidenceNote =
    turnover?.confidenceNote != null
      ? String(turnover.confidenceNote)
      : null;

  const displayBlockedReason =
    turnover?.metadata?.displayBlockedReason != null
      ? String(turnover.metadata.displayBlockedReason)
      : !scoreReady && turnover?.summary
        ? String(turnover.summary)
        : null;

  const re = turnover?.riskExplanation && typeof turnover.riskExplanation === 'object'
    ? turnover.riskExplanation
    : null;

  const primaryDriver = trimDriver(re?.primaryDriver);
  const secondaryDrivers = [];
  if (Array.isArray(re?.secondaryDrivers)) {
    for (const d of re.secondaryDrivers) {
      const row = trimDriver(d);
      if (row) secondaryDrivers.push(row);
    }
  }

  const protectiveFactors = [];
  if (Array.isArray(re?.positiveSignals)) {
    for (const d of re.positiveSignals) {
      const row = trimDriver(d);
      if (row) protectiveFactors.push(row);
    }
  }

  const narrative =
    re?.narrative && String(re.narrative).trim()
      ? String(re.narrative).trim()
      : turnover?.summary && String(turnover.summary).trim()
        ? String(turnover.summary).trim()
        : null;

  const riskReasons = Array.isArray(re?.riskReasons)
    ? re.riskReasons.map((r) => String(r).trim()).filter(Boolean)
    : Array.isArray(turnover?.riskReasons)
      ? turnover.riskReasons.map((r) => String(r).trim()).filter(Boolean)
      : [];

  const generatedAt =
    turnover?.metadata?.generatedAt ??
    turnover?.metadata?.evaluatedAt ??
    employee?.risk_score_last_computed_at ??
    null;

  const freshness = computeRiskScoreFreshness(
    employee
      ? {
          ...employee,
          risk_score: employee.risk_score ?? riskScore,
          risk_score_payload: turnover || employee.risk_score_payload,
        }
      : null
  );

  return {
    employeeId,
    employeeName,
    riskScore,
    riskLevel,
    scoreReady,
    processingState,
    riskScoreStatus: freshness.riskScoreStatus,
    freshnessStatus: freshness.freshnessStatus,
    isFresh: freshness.isFresh,
    isStale: freshness.isStale,
    lastUpdatedAt: freshness.lastUpdatedAt,
    freshnessAgeMs: freshness.ageMs,
    confidenceLevel,
    confidenceNote,
    eligibilityStatus,
    errorCategory,
    displayBlockedReason,
    primaryDriver,
    secondaryDrivers,
    protectiveFactors,
    narrative,
    summary: turnover?.summary != null ? String(turnover.summary) : null,
    riskReasons,
    generatedAt: generatedAt != null ? String(generatedAt) : null,
    inputHash: employee?.risk_score_input_hash != null ? String(employee.risk_score_input_hash) : null,
    source:
      scoreReady && freshness.isFresh
        ? 'persisted'
        : ['processing', 'queued', 'retrying'].includes(processingState)
          ? 'processing'
          : freshness.isStale && scoreReady
            ? 'stale'
            : 'pending',
  };
}

/**
 * Turnover-shaped view for legacy components (RiskScore, briefs) from canonical state.
 * @param {object} riskState
 * @returns {object|null}
 */
function turnoverViewFromRiskState(riskState) {
  if (!riskState || typeof riskState !== 'object') return null;
  return {
    employeeId: riskState.employeeId,
    employeeName: riskState.employeeName,
    score: riskState.riskScore,
    level: riskState.riskLevel,
    scoreReady: riskState.scoreReady,
    confidenceLevel: riskState.confidenceLevel,
    confidenceNote: riskState.confidenceNote,
    summary: riskState.summary,
    riskReasons: riskState.riskReasons,
    riskExplanation: {
      primaryDriver: riskState.primaryDriver,
      secondaryDrivers: riskState.secondaryDrivers,
      positiveSignals: riskState.protectiveFactors,
      narrative: riskState.narrative,
    },
    metadata: {
      generatedAt: riskState.generatedAt,
      processingState: riskState.processingState,
      evaluatedAt: riskState.generatedAt,
      displayBlockedReason: riskState.displayBlockedReason,
      errorCategory: riskState.errorCategory,
      eligibilityStatus: riskState.eligibilityStatus,
      confidenceLevel: riskState.confidenceLevel,
    },
  };
}

module.exports = {
  buildEmployeeRiskState,
  turnoverViewFromRiskState,
  trimDriver,
};
